import { useState, useEffect, useCallback, useRef } from "react";
import { fetchPatients } from "../api/Patientsapi";

/**
 * Hook for searching + paginating the patient list.
 * Returns live-search results with debounce.
 */
export function usePatients() {
  const [query,      setQuery]      = useState("");
  const [page,       setPage]       = useState(1);
  const [patients,   setPatients]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const debounceRef  = useRef(null);
  const controllerRef = useRef(null);

  const load = useCallback((q, p) => {
    // abort any in-flight request
    if (controllerRef.current) controllerRef.current.abort();
    const ctrl = new AbortController();
    controllerRef.current = ctrl;

    setLoading(true);
    setError(null);

    fetchPatients(p, q, ctrl.signal)
      .then(({ patients, page, totalPages, total }) => {
        setPatients(patients);
        setPage(page);
        setTotalPages(totalPages);
        setTotal(total);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // debounce search — reset to page 1 on new query
  const handleSearch = useCallback((q) => {
    setQuery(q);
    setPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q, 1), 350);
  }, [load]);

  const goToPage = useCallback((p) => {
    setPage(p);
    load(query, p);
  }, [load, query]);

  // initial load
  useEffect(() => { load("", 1); }, [load]);

  return {
    query, patients, page, totalPages, total,
    loading, error,
    handleSearch, goToPage,
  };
}